import React from 'react';
import { Box, Typography } from '@mui/material';

export type VitalIndicator = { emoji: string; label: string; color: string; bg: string };

export const getVitalIndicator = (field: string, val: string): VitalIndicator | null => {
  if (!val || val.trim() === '') return null;
  const n = parseFloat(val);
  if (isNaN(n)) return null;

  const normal   = (label = 'Normal'): VitalIndicator => ({ emoji: '🔵', label, color: '#0288d1', bg: '#e1f5fe' });
  const warning  = (label: string):    VitalIndicator => ({ emoji: '🟡', label, color: '#e65100', bg: '#fff3e0' });
  const critical = (label: string):    VitalIndicator => ({ emoji: '🔴', label, color: '#d32f2f', bg: '#ffebee' });

  switch (field) {
    case 'systolicBP':
      if (n < 60 || n > 200) return null;
      if (n < 70)   return critical('Critical Low');
      if (n < 90)   return warning('Hypotension');
      if (n <= 119) return normal();
      if (n <= 129) return warning('Elevated');
      if (n <= 139) return critical('High – Stage 1');
      return critical('Hypertension');

    case 'diastolicBP':
      if (n < 40 || n > 120) return null;
      if (n < 50)  return critical('Critical Low');
      if (n < 60)  return warning('Low');
      if (n <= 79) return normal();
      if (n <= 89) return warning('Elevated');
      return critical('Hypertension');

    case 'pulse':
      if (n < 40 || n > 180) return null;
      if (n < 50)   return critical('Bradycardia');
      if (n < 60)   return warning('Low');
      if (n <= 100) return normal();
      if (n <= 119) return warning('Tachycardia');
      return critical('Tachycardia');

    case 'temperature':
      if (n < 90 || n > 110) return null;
      if (n < 95)   return critical('Hypothermia');
      if (n <= 99)  return normal();
      if (n <= 103) return warning('Fever');
      return critical('High Fever');

    case 'spO2':
      if (n < 70 || n > 100) return null;
      if (n <= 89) return critical('Hypoxia');
      if (n <= 94) return warning('Mild Hypoxia');
      return normal();

    case 'respiratoryRate':
      if (n < 8 || n > 60) return null;
      if (n < 10)  return critical('Bradypnea');
      if (n < 12)  return warning('Low');
      if (n <= 20) return normal();
      if (n <= 30) return warning('Tachypnea');
      return critical('Tachypnea');

    default:
      return null;
  }
};

export function IndicatorBadge({ field, value }: { field: string; value: string }) {
  const ind = getVitalIndicator(field, value);
  if (!ind) return null;
  return (
    <Box sx={{ mt: 0.75, px: 1, py: 0.4, borderRadius: 1, bgcolor: ind.bg, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: ind.color, fontWeight: 700 }}>
        {ind.emoji} {ind.label}
      </Typography>
    </Box>
  );
}
